import { Modal, Text, Button, Group, Stack, Checkbox, TextInput, ScrollArea, Divider } from "@mantine/core";
import { IconSearch, IconUsers } from "@tabler/icons-react";
import { useState, useMemo, useEffect, useCallback } from "react";
import useEmbaixadoresPessoas from "../../hooks/useEmbaixadoresPessoas";

interface EmbaixadorSelectorModalProperties {
  opened: boolean;
  onClose: () => void;
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  title?: string;
}

const EmbaixadorSelectorModal = ({
  opened,
  onClose,
  selectedIds,
  onSelectionChange,
  title = "Selecionar Embaixadores"
}: EmbaixadorSelectorModalProperties) => {
  const [searchValue, setSearchValue] = useState("");
  const [temporarySelectedIds, setTemporarySelectedIds] = useState<string[]>(selectedIds);
  const selectedIdsKey = JSON.stringify(selectedIds);
  const [prevSelectedIdsKey, setPrevSelectedIdsKey] = useState(selectedIdsKey);
  const [prevOpened, setPrevOpened] = useState(opened);
  const { data: embaixadores = [], isLoading } = useEmbaixadoresPessoas();

  if (selectedIdsKey !== prevSelectedIdsKey || opened !== prevOpened) {
    setPrevSelectedIdsKey(selectedIdsKey);
    setPrevOpened(opened);
    setTemporarySelectedIds(selectedIds);
  }

  const filteredEmbaixadores = useMemo(() => {
    if (!searchValue.trim()) return embaixadores;

    const search = searchValue.toLowerCase();
    return embaixadores.filter(
      (embaixador) =>
        embaixador.nome.toLowerCase().includes(search) ||
        embaixador.email?.toLowerCase().includes(search) ||
        embaixador.telefone?.includes(search)
    );
  }, [embaixadores, searchValue]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.currentTarget.value);
  }, []);

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const embaixadorId = event.currentTarget.dataset.embaixadorId;
      if (!embaixadorId) return;

      const newSelection = temporarySelectedIds.includes(embaixadorId)
        ? temporarySelectedIds.filter((id) => id !== embaixadorId)
        : [...temporarySelectedIds, embaixadorId];
      setTemporarySelectedIds(newSelection);
    },
    [temporarySelectedIds]
  );

  const handleSelectAll = useCallback(() => {
    const allIds = filteredEmbaixadores.map((emb) => emb.id);
    const newSelection = [...new Set([...temporarySelectedIds, ...allIds])];
    setTemporarySelectedIds(newSelection);
  }, [filteredEmbaixadores, temporarySelectedIds]);

  const handleDeselectAll = useCallback(() => {
    const filteredIds = new Set(filteredEmbaixadores.map((emb) => emb.id));
    const newSelection = temporarySelectedIds.filter((id) => !filteredIds.has(id));
    setTemporarySelectedIds(newSelection);
  }, [filteredEmbaixadores, temporarySelectedIds]);

  const handleCancel = useCallback(() => {
    setTemporarySelectedIds(selectedIds); // Reverte para seleção original
    onClose();
  }, [selectedIds, onClose]);

  const handleConfirm = useCallback(() => {
    onSelectionChange(temporarySelectedIds); // Aplica as alterações
    onClose();
  }, [temporarySelectedIds, onSelectionChange, onClose]);

  const selectedCount = embaixadores.filter((emb) => temporarySelectedIds.includes(emb.id)).length;
  const filteredSelectedCount = filteredEmbaixadores.filter((emb) => temporarySelectedIds.includes(emb.id)).length;

  useEffect(() => {
    if (!opened) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !opened) return;

      event.stopPropagation();
      event.preventDefault();
      handleCancel();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [opened, handleCancel]);

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <IconUsers size="1.2rem" />
          <Text fw={500}>{title}</Text>
          {selectedCount > 0 && (
            <Text size="sm" c="dimmed">
              ({selectedCount} selecionados)
            </Text>
          )}
        </Group>
      }
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
      zIndex={1001}
      closeOnClickOutside={false}
      closeOnEscape={false}
      trapFocus={true}
    >
      <Stack>
        <TextInput
          placeholder="Buscar por nome, email ou telefone..."
          leftSection={<IconSearch size="1rem" />}
          value={searchValue}
          onChange={handleSearchChange}
        />

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {filteredEmbaixadores.length} embaixadores encontrados
            {filteredSelectedCount > 0 && ` • ${filteredSelectedCount} selecionados nesta busca`}
          </Text>

          <Group gap="xs">
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={handleSelectAll}
              disabled={filteredEmbaixadores.length === 0}
            >
              Selecionar Todos
            </Button>
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={handleDeselectAll}
              disabled={filteredSelectedCount === 0}
            >
              Desmarcar Todos
            </Button>
          </Group>
        </Group>

        <Divider />

        <ScrollArea h={400}>
          {isLoading ? (
            <Text ta="center" c="dimmed" py="xl">
              Carregando embaixadores...
            </Text>
          ) : filteredEmbaixadores.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              {searchValue ? "Nenhum embaixador encontrado" : "Nenhum embaixador cadastrado"}
            </Text>
          ) : (
            <Stack gap="xs">
              {filteredEmbaixadores.map((embaixador) => (
                <Checkbox
                  key={embaixador.id}
                  checked={temporarySelectedIds.includes(embaixador.id)}
                  onChange={handleToggle}
                  data-embaixador-id={embaixador.id}
                  label={
                    <Stack gap={2}>
                      <Text size="sm" fw={500}>
                        {embaixador.nome}
                      </Text>
                      <Group gap="md">
                        {embaixador.email && (
                          <Text size="xs" c="dimmed">
                            {embaixador.email}
                          </Text>
                        )}
                        {embaixador.telefone && (
                          <Text size="xs" c="dimmed">
                            {embaixador.telefone}
                          </Text>
                        )}
                      </Group>
                    </Stack>
                  }
                  styles={{
                    body: { alignItems: "flex-start" },
                    labelWrapper: { width: "100%" }
                  }}
                />
              ))}
            </Stack>
          )}
        </ScrollArea>

        <Divider />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm}>Confirmar Seleção ({selectedCount})</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EmbaixadorSelectorModal;
